import type { UserBooking } from '@/hooks/useMyBookings';
import type { BookingStatus } from '@/types/events';

export interface BookingGroup {
  key: string;
  eventId: string;
  occurrenceKey: string;
  status: BookingStatus;
  startAt: string;
  title: string;
  producerName: string;
  totalSpots: number;
  items: UserBooking[];
}

/**
 * Groups bookings by event + occurrence (or start date fallback) + status.
 * Returns groups sorted by startAt asc, then title asc.
 * Items within each group are sorted by created_at asc.
 */
export function groupBookings(bookings: UserBooking[]): BookingGroup[] {
  const map = new Map<string, BookingGroup>();

  for (const b of bookings) {
    const occKey = b.occurrence_id ?? b.event_start_date ?? b.event_id;
    const key = `${b.event_id}::${occKey}::${b.status}`;

    const existing = map.get(key);
    if (existing) {
      existing.totalSpots += b.spots;
      existing.items.push(b);
    } else {
      map.set(key, {
        key,
        eventId: b.event_id,
        occurrenceKey: occKey,
        status: b.status,
        startAt: b.event_start_date,
        title: b.event_title,
        producerName: b.producer_name,
        totalSpots: b.spots,
        items: [b],
      });
    }
  }

  const groups = Array.from(map.values());

  // Sort items within each group by created_at asc
  for (const g of groups) {
    g.items.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  // Sort groups by startAt asc, then title asc
  groups.sort((a, b) => {
    const dateCompare = a.startAt.localeCompare(b.startAt);
    if (dateCompare !== 0) return dateCompare;
    return a.title.localeCompare(b.title);
  });

  return groups;
}
